import type { HttpContext, HttpEvent } from '@angular/common/http';
import { DestroyRef, Injectable, Injector, type Signal, inject } from '@angular/core';
import { Observable, type OperatorFunction, type Subscriber, filter, map } from 'rxjs';
import { isHeicFile } from '../entities/image-format.config';
import { type UploadLane, isEmptyUploadAnswer } from '../entities/upload-lane';
import { MAX_ATTEMPTS, TRANSPORT_SLOTS } from '../entities/upload-retry-policy';
import { UploadTransportService } from '../infrastructure/upload-transport.service';
import { UploadCircuitBreaker } from './upload-circuit-breaker';
import { UploadQueue, type UploadQueueHooks, type UploadQueueHost } from './upload-queue';

export class UploadAbortedError extends Error {
  constructor() {
    super('upload aborted');
    this.name = 'UploadAbortedError';
  }
}

export interface UploadRequest<M, R> {
  readonly lane: UploadLane<M, R>;
  /** Slot group: a project, a book, a gallery. Every key gets its own slots. */
  readonly key: string;
  readonly file: File;
  readonly meta: M;
  readonly headers?: Readonly<Record<string, string>>;
  readonly context?: HttpContext;
  readonly onProgress?: (percent: number, loaded: number, total: number) => void;
  readonly onWaiting?: (wait: { readonly attempts: number; readonly backoffMs: number; readonly offline: boolean }) => void;
  readonly signal?: AbortSignal;
}

export type UploadEvent<R> =
  | { readonly type: 'progress'; readonly percent: number; readonly loaded: number; readonly total: number }
  | { readonly type: 'waiting'; readonly attempts: number; readonly backoffMs: number; readonly offline: boolean }
  | { readonly type: 'done'; readonly result: R | null };

export type UploadSettled<R> =
  | { readonly status: 'done'; readonly result: R | null }
  | { readonly status: 'failed'; readonly error: unknown };

export function uploadResult<R>(): OperatorFunction<UploadEvent<R>, R | null> {
  return source =>
    source.pipe(
      filter((event): event is Extract<UploadEvent<R>, { type: 'done' }> => event.type === 'done'),
      map(event => event.result),
    );
}

interface OneOffTask {
  readonly key: string;
  readonly subscriber: Subscriber<UploadEvent<unknown>>;
  readonly send: () => Observable<HttpEvent<unknown>>;
  readonly read: (body: unknown) => unknown;
  readonly maxAttempts: number;
  readonly onProgress?: (percent: number, loaded: number, total: number) => void;
  readonly onWaiting?: UploadRequest<unknown, unknown>['onWaiting'];
  body: unknown;
  empty: boolean;
}

/** The queue host of single-file requests: the task table IS the rows. */
class OneOffHost implements UploadQueueHost {
  readonly tasks = new Map<string, OneOffTask>();
  private readonly ready = new Set<string>();
  readonly isEmptyAnswer = isEmptyUploadAnswer;
  readonly hooks: UploadQueueHooks = {
    started: id => void this.ready.delete(id),
    progress: (id, percent, loaded, total) => {
      const task = this.tasks.get(id);
      if (!task) return;
      task.onProgress?.(percent, loaded, total);
      task.subscriber.next({ type: 'progress', percent, loaded, total });
    },
    answer: (id, body) => {
      const task = this.tasks.get(id);
      if (task) task.body = body;
    },
    emptyAnswer: id => {
      const task = this.tasks.get(id);
      if (task) task.empty = true;
    },
    completed: id => {
      const task = this.tasks.get(id);
      if (!task) return;
      this.remove(id);
      task.subscriber.next({ type: 'done', result: task.empty ? null : task.read(task.body) });
      task.subscriber.complete();
    },
    retryScheduled: (id, wait) => {
      const task = this.tasks.get(id);
      if (!task) return;
      task.onWaiting?.(wait);
      task.subscriber.next({ type: 'waiting', ...wait });
    },
    requeued: id => void this.ready.add(id),
    failed: (id, failure) => {
      const task = this.tasks.get(id);
      if (!task) return;
      this.remove(id);
      task.subscriber.error(failure.error);
    },
  };

  add(id: string, task: OneOffTask): void {
    this.tasks.set(id, task);
    this.ready.add(id);
  }

  remove(id: string): boolean {
    this.ready.delete(id);
    return this.tasks.delete(id);
  }

  readyIds(key: string): readonly string[] {
    return [...this.ready].filter(id => this.tasks.get(id)?.key === key);
  }

  open(id: string): Observable<HttpEvent<unknown>> | null {
    return this.tasks.get(id)?.send() ?? null;
  }

  maxAttempts(id: string): number {
    return this.tasks.get(id)?.maxAttempts ?? MAX_ATTEMPTS;
  }
}

@Injectable({ providedIn: 'root' })
export class UploadEngine {
  private readonly injector = inject(Injector);
  private readonly circuit = new UploadCircuitBreaker();
  private readonly queues = new Set<UploadQueue>();
  private readonly unloadGuards = new Set<() => boolean>();
  private readonly previews = new Map<string, string>();
  private readonly oneOff = new OneOffHost();
  private readonly oneOffQueue = this.openQueue(this.oneOff);
  readonly circuitPaused: Signal<boolean> = this.circuit.paused;

  constructor() {
    // Bytes that have not reached the server die with the tab - the browser asks first.
    const onBeforeUnload = (event: Event): void => {
      if ([...this.unloadGuards].some(isBusy => isBusy())) event.preventDefault();
    };

    window.addEventListener('beforeunload', onBeforeUnload);
    this.guardUnload(() => this.oneOff.tasks.size > 0);
    inject(DestroyRef).onDestroy(() => {
      window.removeEventListener('beforeunload', onBeforeUnload);
      this.circuit.destroy();
      for (const id of [...this.previews.keys()]) this.releasePreview(id);
    });
  }

  openQueue(host: UploadQueueHost, options?: { readonly slots?: number }): UploadQueue {
    const queue = new UploadQueue(host, this.circuit, options?.slots ?? TRANSPORT_SLOTS);
    this.queues.add(queue);
    return queue;
  }

  upload<M, R>(request: UploadRequest<M, R>): Observable<UploadEvent<R>> {
    return new Observable<UploadEvent<R>>(subscriber => {
      if (request.signal?.aborted) {
        subscriber.error(new UploadAbortedError());
        return undefined;
      }

      const id = crypto.randomUUID();
      const { lane, file, meta, headers, context, key } = request;
      this.oneOff.add(id, {
        key,
        subscriber: subscriber as unknown as Subscriber<UploadEvent<unknown>>,
        send: () => this.transport.post(lane, file, meta, { headers, context }),
        read: lane.result,
        maxAttempts: lane.idempotent ? MAX_ATTEMPTS : 1,
        onProgress: request.onProgress,
        onWaiting: request.onWaiting,
        body: undefined,
        empty: false,
      });
      const onAbort = (): void => {
        this.drop(id, key);
        subscriber.error(new UploadAbortedError());
      };

      request.signal?.addEventListener('abort', onAbort, { once: true });
      this.oneOffQueue.pump(key);
      return () => {
        request.signal?.removeEventListener('abort', onAbort);
        this.drop(id, key);
      };
    });
  }

  uploadAndWait<M, R>(request: UploadRequest<M, R>): Promise<R | null> {
    return new Promise<R | null>((resolve, reject) => {
      let result: R | null = null;
      this.upload(request).subscribe({
        next: event => {
          if (event.type === 'done') result = event.result;
        },
        error: (error: unknown) => reject(error),
        complete: () => resolve(result),
      });
    });
  }

  /** Runs the requests through the slots and settles every one; `onSettled` fires as each lands, not at the end. */
  uploadBatch<M, R>(
    requests: readonly UploadRequest<M, R>[],
    onSettled?: (index: number, settled: UploadSettled<R>) => void,
  ): Promise<readonly UploadSettled<R>[]> {
    return Promise.all(
      requests.map(async (request, index): Promise<UploadSettled<R>> => {
        let settled: UploadSettled<R>;
        try {
          settled = { status: 'done', result: await this.uploadAndWait(request) };
        } catch (error) {
          settled = { status: 'failed', error };
        }

        onSettled?.(index, settled);
        return settled;
      }),
    );
  }

  cancelKey(key: string): void {
    for (const [id, task] of [...this.oneOff.tasks]) {
      if (task.key !== key) continue;
      this.drop(id, key);
      task.subscriber.error(new UploadAbortedError());
    }
  }

  resume(): void {
    this.circuit.resume();
  }

  retryNow(key?: string): void {
    this.circuit.resume();
    for (const queue of this.queues) queue.skipBackoff(key);
  }

  guardUnload(isBusy: () => boolean): () => void {
    this.unloadGuards.add(isBusy);
    return () => void this.unloadGuards.delete(isBusy);
  }

  trackPreview(id: string, file: File): string | null {
    // The browser cannot draw a HEIC: the tile waits for the server's JPEG version instead.
    if (isHeicFile(file)) return null;
    try {
      const url = URL.createObjectURL(file);
      this.previews.set(id, url);
      return url;
    } catch {
      return null;
    }
  }

  hasPreview(id: string): boolean {
    return this.previews.has(id);
  }

  releasePreview(id: string): void {
    const url = this.previews.get(id);
    if (!url) return;
    this.previews.delete(id);
    try {
      URL.revokeObjectURL(url);
    } catch {
      // A browser that never handed one out has nothing to reclaim.
    }
  }

  private get transport(): UploadTransportService {
    return this.injector.get(UploadTransportService);
  }

  private drop(id: string, key: string): void {
    if (!this.oneOff.remove(id)) return;
    this.oneOffQueue.abort(id, key);
  }
}
